import { TextEditor, validators } from '@approvalmax/ui';
import { FC, FormEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { amplitudeService } from 'services/amplitude';
import { routingService } from 'services/routing';
import { getPath, Path } from 'urlBuilder';

import { signup } from '../../../../actions';
import { SignupModuleData } from '../../../../reducers/page/moduleReducer';
import { getAuthCommand } from '../../../../selectors/commandSelectors';
import { getModule } from '../../../../selectors/pageSelectors';
import { messages } from './SignupModule.messages';
import {
    AdvantageBlock,
    AdvantageIcon,
    AdvantagesArea,
    Checkboxes,
    Container,
    EditorContainer,
    ExperimentalConsent,
    LastCheckboxEditor,
    Root,
    SignupButton,
    Title,
    TrialTitle,
    ValidationMessage,
} from './SignupModule.styles';
import { SignupModuleProps } from './SignupModule.types';

const SignupModule: FC<SignupModuleProps> = memo((props) => {
    const { setEmail } = props;

    const emailInputRef = useRef<HTMLInputElement>(null);

    const authCommand = useSelector(getAuthCommand);
    const module = useSelector(getModule<SignupModuleData>);

    const [loginText, setLoginText] = useState(module.email || '');
    const [loginTouched, setLoginTouched] = useState(false);
    const [consentMarketingAccepted, setConsentMarketingAccepted] = useState(false);

    const dispatch = useDispatch();

    const getNormalisedEmail = useCallback(() => {
        return loginText.trim();
    }, [loginText]);

    useEffect(() => {
        if (module.success) {
            routingService.push(
                getPath(Path.emailConfirmation, {
                    email: getNormalisedEmail(),
                    app: module.app,
                    consentMarketingAccepted,
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [module.success]);

    const onConsentMarketingChange = useCallback((value: boolean) => {
        setConsentMarketingAccepted(value);
    }, []);

    const validateEmail = useCallback(() => {
        let emailValidationError;

        const email = getNormalisedEmail();

        if (validators.required(email)) {
            emailValidationError = messages.emailRequired;
        } else if (validators.email(email)) {
            emailValidationError = messages.emailInvalid;
        }

        return emailValidationError;
    }, [getNormalisedEmail]);

    const onChangeLogin = useCallback(
        (value: string) => {
            setLoginText(value.trim());
            setEmail(value.trim());
        },
        [setEmail]
    );

    const signUp = useCallback(() => {
        setLoginTouched(true);

        if (validateEmail()) {
            if (emailInputRef.current) {
                emailInputRef.current.focus();
            }

            return;
        }

        amplitudeService.sendData('signup: click on sign up button', {
            'signup type': 'email',
        });

        dispatch(signup(getNormalisedEmail(), consentMarketingAccepted, module.app));
    }, [consentMarketingAccepted, dispatch, getNormalisedEmail, module.app, validateEmail]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            signUp();
        },
        [signUp]
    );

    let emailValidationError;

    if (loginTouched) {
        emailValidationError = validateEmail();
    }

    return (
        <Root>
            <TrialTitle>{messages.startTrial}</TrialTitle>

            <AdvantagesArea>
                <AdvantageBlock>
                    <AdvantageIcon width={11} height={8} />

                    <span>{messages.advantages1}</span>
                </AdvantageBlock>

                <AdvantageBlock>
                    <AdvantageIcon width={11} height={8} />

                    <span>{messages.advantages2}</span>
                </AdvantageBlock>

                <AdvantageBlock>
                    <AdvantageIcon width={11} height={8} />

                    <span>{messages.advantages3}</span>
                </AdvantageBlock>
            </AdvantagesArea>

            <Container onSubmit={onSubmit}>
                <Title>{messages.title}</Title>

                <EditorContainer>
                    <TextEditor
                        ref={emailInputRef}
                        value={loginText}
                        type='text'
                        name='email'
                        autoComplete='on'
                        spellCheck={false}
                        theme='auth'
                        onChange={onChangeLogin}
                        invalid={Boolean(emailValidationError)}
                        placeholder={emailValidationError ? '' : messages.emailPlaceholder}
                        focusOnMount
                    />

                    {emailValidationError && <ValidationMessage>{emailValidationError}</ValidationMessage>}
                </EditorContainer>

                <Checkboxes>
                    <LastCheckboxEditor
                        checkboxAlignment='top'
                        onChange={onConsentMarketingChange}
                        value={consentMarketingAccepted}
                    >
                        {messages.consentMarketingText}
                    </LastCheckboxEditor>
                </Checkboxes>

                <SignupButton padding='narrow20' execute={signUp} command={authCommand}>
                    {messages.signupButtonText}
                </SignupButton>

                <ExperimentalConsent buttonText={messages.signupButtonText} />
            </Container>
        </Root>
    );
});

export default SignupModule;
